import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import TagManager from "react-gtm-module"
import { HiOutlineArrowSmRight } from "react-icons/hi"
import { IoClose } from "react-icons/io5"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import SolidBtn from "@fuse/sharedComponents/buttons/SolidBtn"
import Footer from "@fuse/sharedComponents/footer"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import Navbar from "@fuse/sharedComponents/navbar/Navbar"
import {
  EEVENT_INVITATION_CRITERIA,
  EGENDERS,
  EVENT_CLASS,
  EVENT_STATUS,
  EventStatusType,
} from "@letshang/constants"
import {
  difference,
  filter,
  find,
  get,
  groupBy,
  head,
  isEmpty,
  last,
  map,
  orderBy,
  size,
  uniqBy,
  uniqueId,
} from "lodash-es"
import moment from "moment"
import { useRecoilState, useRecoilValue } from "recoil"
import { useResetRecoilState } from "recoil"

import {
  allEventsSelectors,
  recentViewEventData,
  userVisitedInvitesData,
} from "@fuse/recoil/events"
import { userState } from "@fuse/recoil/signup"
import { userStateForAnalytics } from "@fuse/recoil/signup"

import {
  ELogAnalyticEvent,
  ELogGTMEvent,
  logAnalyticEvent,
} from "@firebaseConfig/analyticsUtil"

import { WEBSITE_URL } from "@settingsAlias/constants"

import { createOrganizationAPI } from "@api/firebaseWrappedApis"
import { usePrimaryEvents } from "@api/services/events/useEventsApi"

import { appStoreLink, playStoreLink } from "@fuse/utils/DeviceDection"
import { isDev } from "@fuse/utils/DeviceDection"

import EventCard from "@fuse/pages/landingPage/components/EventCard"
import GifCarousel from "@fuse/pages/landingPage/components/GifCarousel"

import AppStoreIcon from "@fuse/assets/icons/icon-app-store.svg"
import GooglePlayIcon from "@fuse/assets/icons/icon-google-play.svg"

const bgImage = "/assets/alpha-event-not-found.svg"
export const randomImageUri = `https://picsum.photos/1018/536?random=${uniqueId()}`

enum EEventsTabOptions {
  TODAY = "TODAY",
  UPCOMING = "UPCOMING",
  PAST = "PAST",
  INVITED = "INVITED",
  RECENT = "RECENT",
}

enum EDownloadType {
  PLAYSTORE = "PLAYSTORE",
  APPSTORE = "APPSTORE",
}

const LandingPage = () => {
  const [user] = useRecoilState(userState)
  const [showInvitedBanner, setShowInvitedBanner] = useState(false)

  const uid = get(user, "uid", "")

  const [selectedTab, setSelectedTab] = useState(EEventsTabOptions.TODAY)
  const userStateForAnalyticsValue = useRecoilValue(userStateForAnalytics)
  const isFirstMount = useRef(true)

  const { isLoading, isError, refetch } = usePrimaryEvents(uid)

  const [randomOrg, setRandomOrg] = useState<{
    id: string
    url: string
  } | null>(null)

  useEffect(() => {
    // change url
    const url = window.location.origin
    // remove old query params
    const newUrl = url.split("?")[0] + "/home"
    window.history.pushState({}, "", newUrl)
  }, [])

  const stableRefetch = useCallback(() => {
    if (uid) {
      refetch()
    }
  }, [uid, refetch])

  useEffect(() => {
    stableRefetch()
  }, [stableRefetch])

  const events = useRecoilValue(allEventsSelectors)
  const recentEvents = useRecoilValue(recentViewEventData)
  const [userVisitedInvites, setUserVisitedInvites] = useRecoilState(
    userVisitedInvitesData,
  )

  const today = moment().local().startOf("day")

  useEffect(() => {
    logAnalyticEvent(ELogAnalyticEvent.LANDING_WEB, {
      uid: uid,
      page_name: "LandingPage",
    })
  }, [userStateForAnalyticsValue])

  useEffect(() => {
    // Send custom event to GTM
    TagManager.dataLayer({
      dataLayer: {
        event: ELogGTMEvent.PAGE_VIEW,
        page_name: "LandingPage",
        page_title: document.title,
        page_path: window.location.pathname,
        page_referrer: document.referrer,
        page_url: window.location.href,
        page_url_query: window.location.search,
        user_id: user?.id,
      },
    })
  }, [])

  const { todaysEvent, upcomingEvent, pastEvents, allDrafts, invitedEvents } =
    useMemo(() => {
      const drafts = uniqBy(
        filter(events, { eventStatus: EVENT_STATUS.DRAFT }),
        "eventId",
      )

      const invited = uniqBy(
        filter(events, { myJoinStatus: EventStatusType.INVITED }),
        "eventId",
      )

      const categorized = groupBy(
        events.filter((e) => e.eventStatus !== EVENT_STATUS.DRAFT),
        (event) => {
          const eventDate = moment(event.timing.eventDate)
            .local()
            .startOf("day")
          const eventEndTime = moment(event.timing.eventEndTime).local()

          if (
            eventDate.isSame(today, "day") &&
            eventEndTime.isAfter(moment())
          ) {
            return "todaysEvent"
          }
          if (eventDate.isAfter(today, "day")) {
            return "upcomingEvent"
          }
          if (eventDate.isBefore(today, "day")) {
            return "pastEvents"
          }
          return null
        },
      )

      return {
        todaysEvent: uniqBy(categorized.todaysEvent || [], "eventId"),
        upcomingEvent: uniqBy(categorized.upcomingEvent || [], "eventId"),
        pastEvents: uniqBy(categorized.pastEvents || [], "eventId"),
        allDrafts: drafts,
        invitedEvents: invited,
      }
    }, [events, today])

  useEffect(() => {
    if (!isEmpty(invitedEvents)) {
      const invitedEventsId = map(invitedEvents, "eventId")
      const unvisitedInvites = difference(invitedEventsId, userVisitedInvites)

      if (!isEmpty(unvisitedInvites)) {
        setShowInvitedBanner(true)
      }
    }
  }, [invitedEvents])

  const onVisitedInvitedEvents = () => {
    setSelectedTab(EEventsTabOptions.INVITED)
    const invitedEventsId = map(invitedEvents, "eventId")
    const unvisitedInvites = difference(invitedEventsId, userVisitedInvites)

    const updatedUserVisitedInvites = [
      ...userVisitedInvites,
      ...unvisitedInvites,
    ]
    setUserVisitedInvites(updatedUserVisitedInvites)
    setShowInvitedBanner(false)
  }

  const eventsTabOptions = [
    {
      title: "Today's event",
      data: [...todaysEvent, { isEmptySection: true }],
      type: EEventsTabOptions.TODAY,
      isSelected: selectedTab === EEventsTabOptions.TODAY,
      showCount: false,
    },
    {
      title: "Upcoming events",
      data: [...upcomingEvent, { isEmptySection: true }],
      type: EEventsTabOptions.UPCOMING,
      isSelected: selectedTab === EEventsTabOptions.UPCOMING,
      showCount: false,
    },
    ...(isEmpty(invitedEvents)
      ? []
      : [
          {
            title: "Invites",
            data: invitedEvents,
            type: EEventsTabOptions.INVITED,
            isSelected: selectedTab === EEventsTabOptions.INVITED,
            showCount: true,
            dataCount: size(invitedEvents),
          },
        ]),
    ...(isEmpty(pastEvents)
      ? []
      : [
          {
            title: "Past events",
            data: pastEvents,
            type: EEventsTabOptions.PAST,
            isSelected: selectedTab === EEventsTabOptions.PAST,
            showCount: false,
          },
        ]),
    ...(isEmpty(recentEvents)
      ? []
      : [
          {
            title: "Recently viewed",
            data: recentEvents.filter(
              (event) =>
                event?.eventDocData?.eventStatus !== EVENT_STATUS.DRAFT,
            ),
            type: EEventsTabOptions.RECENT,
            isSelected: selectedTab === EEventsTabOptions.RECENT,
            showCount: false,
          },
        ]),
  ]

  useEffect(() => {
    if (!isFirstMount.current) return
    isFirstMount.current = false

    const tabsWithData = [
      { type: EEventsTabOptions.TODAY, data: todaysEvent },
      { type: EEventsTabOptions.UPCOMING, data: upcomingEvent },
      { type: EEventsTabOptions.INVITED, data: invitedEvents },
      { type: EEventsTabOptions.PAST, data: pastEvents },
      { type: EEventsTabOptions.RECENT, data: recentEvents },
    ]

    const firstTab = get(
      find(tabsWithData, (tab) => !isEmpty(tab.data)),
      "type",
      EEventsTabOptions.TODAY,
    )

    setSelectedTab(firstTab)
  }, [])

  // console.log("RECENT EVENTS", recentEvents)

  const onDownloadLink = (type: EDownloadType) => {
    if (type === EDownloadType.APPSTORE) {
      window.open(appStoreLink, "_blank")
    } else if (type === EDownloadType.PLAYSTORE) {
      window.open(playStoreLink, "_blank")
    }
  }

  const selectedTabData = get(
    find(eventsTabOptions, { isSelected: true }),
    "data",
  )

  const noEventsFound =
    isEmpty(todaysEvent) && isEmpty(upcomingEvent) && isEmpty(pastEvents)

  const UserInvitesBanner = () => {
    if (showInvitedBanner) {
      const inviteSize = size(invitedEvents)
      const firstInvite = head(invitedEvents)
      const firstInviteHostName = get(firstInvite, "initiatorName", "")
      const firstInviteEventName = get(firstInvite, "eventName", "")

      return (
        <div className="flex w-full items-center mt-8 rounded-2xl overflow-hidden px-5 lg:px-10 py-4 gap-8 bg-white/10">
          <div className="flex flex-col w-full items-start">
            <p className="text-white text-lg text-start">
              You have {inviteSize} invites
            </p>
            <p className="text-white/35 mt-1 text-sm lg:text-base text-start">
              {firstInviteHostName} invites you to "{firstInviteEventName}"{" "}
              {inviteSize - 1 > 0 && <span>+{inviteSize - 1} others</span>}
            </p>
          </div>
          <div className="flex flex-row mr-4">
            <div className="flex">
              <div
                className="h-7 w-7 rounded-full bg-white/10 flex items-center justify-center cursor-pointer"
                onClick={() => {
                  onVisitedInvitedEvents()
                }}
              >
                <HiOutlineArrowSmRight className="text-white" />
              </div>
            </div>
          </div>
        </div>
      )
    } else {
      return null
    }
  }

  const createRandomOrg = async () => {
    try {
      // Generate a unique ID for display purposes (the actual ID will be generated on the server)
      const randomSuffix = Math.random().toString(36).substring(2, 10)

      // Define organization data
      const orgData = {
        name: `Test Organization ${randomSuffix}`,
        description: `This is a test organization created for testing purposes with ID: ${randomSuffix}`,
        bio_html: `<p>This is a <strong>test organization</strong> created automatically.</p>
                  <p>It was created as a test object for viewing organization profiles.</p>`,
        page_html: `<h2>About Test Organization ${randomSuffix}</h2>
                  <p>This organization doesn't actually exist - it was created for testing the organization profile feature.</p>
                  <p>Here's what you can do with organizations:</p>
                  <ul>
                    <li>View organization profiles</li>
                    <li>See upcoming and past events</li>
                    <li>Follow organizations you're interested in</li>
                  </ul>`,
      }

      // Create the organization
      const response: any = await createOrganizationAPI(orgData)

      if (response.success) {
        // Get the organization ID from the response
        const orgId = response.data.organizationId

        // Generate the URL
        const orgUrl = `${WEBSITE_URL}/organizations/${orgId}`

        // Update state with the new organization
        setRandomOrg({ id: orgId, url: orgUrl })

        // Log analytics event
        logAnalyticEvent("create_random_org", {
          button_name: "create_random_org",
          org_id: orgId,
        })
      } else {
        console.error("Failed to create organization:", response.message)
        alert("Failed to create organization. Please try again.")
      }
    } catch (error) {
      console.error("Error creating organization:", error)
      alert("An error occurred while creating the organization.")
    }
  }

  // Check if we're in development mode
  const isDevMode = isDev()

  // Check if the user is an admin (you can also add an admin check here)
  const isAdmin = useMemo(() => {
    // Add any admin check logic here if needed
    // For example: return user?.role === 'admin'
    return false // Default non-admin
  }, [user])

  // Show the random org creator only in dev mode or for admins
  const showRandomOrgCreator = isDevMode || isAdmin

  if (!uid) {
    // redirect to letshang.info
    window.location.href = "https://letshang.info"
    return
  }

  // console.log("isLoading", isLoading, noEventsFound)

  return (
    <BackgroundWrapper bgImage={bgImage} className="h-full">
      <BlurWrapper
        blurKey={blurKeys?.mainBlurView}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="h-full flex flex-col overflow-auto min-h-[100vh]"
      >
        <div id="upper-body" className="flex">
          <Navbar />
        </div>
        {/* {isLoading && noEventsFound && <LoadingSpinner />} */}

        <div className={`flex w-full items-center justify-center relative`}>
          <div className="flex w-full flex-col justify-between items-center self-center px-4 md:px-8 max-w-screen-xl 2xl:max-w-screen-2xl">
            <GifCarousel eventData={head(todaysEvent) || head(upcomingEvent)} />
            {/* <GifCarousel
              eventData={{
                ...(head(todaysEvent) || head(upcomingEvent)),
                eventClass: EVENT_CLASS.NON_TICKETED_EVENT,
                invitationCriteria:
                  EEVENT_INVITATION_CRITERIA.INVITE_BY_LINK_ONLY,
                initiatorGender: EGENDERS.RATHER_NOT_SAY,
                eventStatus: EVENT_STATUS.OPEN,
              }}
            /> */}

            {/* Random Organization Creator Button - only visible in dev mode or for admins */}
            {showRandomOrgCreator && (
              <div className="flex w-full items-center mt-8 rounded-2xl overflow-hidden px-5 lg:px-10 py-4 bg-blue-500/20">
                <div className="flex flex-col w-full">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white text-lg lg:text-xl uppercase">
                        Test Organization Profiles
                      </p>
                      <p className="text-white/60 mt-1 text-sm">
                        Create a random organization ID and access its profile
                        page.
                      </p>
                    </div>
                    <button
                      onClick={createRandomOrg}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-full text-sm font-medium transition duration-200"
                    >
                      Create Random Org
                    </button>
                  </div>

                  {randomOrg && (
                    <div className="mt-4 p-3 bg-white/10 rounded-lg">
                      <p className="text-white text-sm mb-1">
                        Organization created!
                      </p>
                      <p className="text-white/60 text-xs mb-2">
                        ID: {randomOrg.id}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-blue-300 text-sm break-all">
                          {randomOrg.url}
                        </p>
                        <button
                          onClick={() => window.open(randomOrg.url, "_blank")}
                          className="bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1 rounded transition duration-200"
                        >
                          Open
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <UserInvitesBanner />
            <div className="flex w-full mt-8 overflow-x-auto">
              <div className="flex gap-4 whitespace-nowrap">
                {eventsTabOptions.map((item, index) => {
                  const isSelected = item.isSelected
                  const showCount = item?.showCount

                  if (showCount) {
                    return (
                      <button
                        key={index.toString()}
                        className={`px-6 py-2 bg-white/10 rounded-full text-sm flex items-center justify-center gap-2 flex-row ${isSelected ? "text-white" : "text-white/50 opacity-70"}`}
                        onClick={() => setSelectedTab(item?.type)}
                      >
                        {item?.title}
                        <div className="flex">
                          <BlurWrapper
                            overlayOpacityDefault="5"
                            className="flex h-4 w-4 rounded-full items-center justify-center overflow-hidden"
                          >
                            <p className="text-[10px] text-center">
                              {get(item, "dataCount", 0)}
                            </p>
                          </BlurWrapper>
                        </div>
                      </button>
                    )
                  } else {
                    return (
                      <button
                        key={index.toString()}
                        className={`px-6 py-2 bg-white/10 rounded-full text-sm ${isSelected ? "text-white" : "text-white/50 opacity-70"}`}
                        onClick={() => setSelectedTab(item?.type)}
                      >
                        {item?.title}
                      </button>
                    )
                  }
                })}
              </div>
            </div>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4 w-full my-8">
              {selectedTabData?.map((item, index) => {
                return (
                  <EventCard
                    key={index.toString()}
                    event={
                      item?.eventDocData
                        ? { ...item.eventDocData, eventId: item.eventId }
                        : item
                    }
                  />
                )
              })}
            </div>
            <Footer />
          </div>
        </div>
      </BlurWrapper>
    </BackgroundWrapper>
  )
}

export default LandingPage
